/**
 * La facture impayée : ce qui peut être réclamé, et la mise en demeure.
 *
 * DEUX RÉGIMES QUE TOUT LE MONDE CONFOND, et c'est là que l'outil sert :
 *
 *   - CLIENT PROFESSIONNEL. Les pénalités de retard courent de plein droit, dès
 *     le lendemain de l'échéance, « sans qu'un rappel soit nécessaire »
 *     (article L. 441-10 du code de commerce). S'y ajoute une indemnité
 *     forfaitaire de 40 € pour frais de recouvrement (article D. 441-5), due
 *     une fois par facture. La lettre ne fait donc que réclamer ce qui est déjà
 *     acquis.
 *
 *   - CLIENT PARTICULIER. Rien de tout cela ne s'applique : le code de commerce
 *     ne régit que les relations entre professionnels. Les intérêts ne courent
 *     qu'À COMPTER DE LA MISE EN DEMEURE, au taux légal, et sans indemnité
 *     forfaitaire (article 1344-1 du code civil), sauf si le contrat prévoit que
 *     la seule échéance vaut mise en demeure (article 1344). Une lettre qui
 *     réclamerait 40 € et des pénalités à un particulier serait fausse, et
 *     l'outil refuse de l'écrire.
 *
 * LA PRESCRIPTION EST LE PREMIER REFUS, avant même le calcul : cinq ans entre
 * professionnels (article L. 110-4 du code de commerce, ou article 2224 du code
 * civil quand aucune des deux parties n'est commerçante), mais DEUX ANS pour un
 * professionnel qui réclame à un consommateur (article L. 218-2 du code de la
 * consommation), un délai que les parties ne peuvent pas allonger.
 *
 * CES DÉLAIS COURENT DE LA FIN DE LA PRESTATION, pas de la facture ni de son
 * échéance. Pour la Cour de cassation, l'obligation de payer « prend naissance
 * au moment où la prestation commandée a été exécutée » (Com., 26 février 2020,
 * n° 18-25.036) : le créancier connaît dès ce jour les faits qui lui permettent
 * d'agir, hors contrat ou loi contraire (1re civ., 19 mai 2021, n° 20-12.520 ;
 * 3e civ., 1er mars 2023, n° 21-23.176). Compter depuis l'échéance, comme
 * l'outil le faisait jusqu'au 14 septembre 2026, laissait croire qu'il restait
 * du temps à quelqu'un dont la créance était déjà prescrite.
 *
 * LES TAUX PÉRIMENT TOUS LES SIX MOIS. Ils sont datés et regroupés dans
 * `TAUX`, avec la source de chacun : le taux légal est fixé par arrêté au
 * 1er janvier et au 1er juillet, et le taux directeur de la Banque centrale
 * européenne bouge au gré de ses décisions. La date de revue du registre est
 * calée sur le 1er janvier.
 */

import { dateEnToutesLettres, eurosEnPhrase, identite, nettoyer } from "./documentText.js";

/**
 * Les taux du semestre, avec ce qui les fixe et la date où ils ont été relevés.
 *
 * `legalCreancierPro` est celui des « autres cas » de l'arrêté : le créancier
 * est ici un professionnel, jamais un particulier. Le taux de 6,84 % réservé
 * aux créanciers personnes physiques ne concerne donc aucun lecteur de cet
 * outil, et l'écrire serait une erreur de plus d'un point.
 */
export const TAUX = {
    legalCreancierPro: 2.75,
    bceRefinancement: 2.4,
    semestre: "second semestre 2026",
    releveLe: "2026-09-08",
    source: "arrêté du 26 juin 2026 pour le taux légal, Banque de France pour le taux directeur du 17 juin 2026",
};

/** L'indemnité forfaitaire pour frais de recouvrement, article D. 441-5. */
export const INDEMNITE_FORFAITAIRE = 40;

/** Le taux supplétif entre professionnels : taux directeur BCE majoré de dix points. */
export const TAUX_SUPPLETIF = Math.round((TAUX.bceRefinancement + 10) * 100) / 100;

/** Le plancher légal d'un taux prévu au contrat : trois fois le taux légal. */
export const TAUX_PLANCHER = Math.round(TAUX.legalCreancierPro * 3 * 100) / 100;

/** Les délais de prescription, en années, selon la qualité du client. Ils courent de la fin de la prestation. */
export const PRESCRIPTION_ANS = { professionnel: 5, particulier: 2 };

/** Le délai laissé au débiteur dans la lettre, en jours. */
export const DELAI_MISE_EN_DEMEURE_JOURS = 15;

/** Une date ISO découpée, ou null si elle n'en est pas une. */
function jour(iso) {
    const t = nettoyer(iso);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return null;
    const [a, m, j] = t.split("-").map(Number);
    const d = new Date(Date.UTC(a, m - 1, j));
    if (d.getUTCFullYear() !== a || d.getUTCMonth() !== m - 1 || d.getUTCDate() !== j) return null;
    return { a, m, j, date: d };
}

/** Le nombre de jours entre deux dates ISO, négatif si la seconde précède. */
export function joursEntre(debut, fin) {
    const a = jour(debut);
    const b = jour(fin);
    if (!a || !b) return null;
    return Math.round((b.date - a.date) / 86400000);
}

/** Un montant en euros, accepté avec virgule ou point. */
export function montant(brut) {
    const t = nettoyer(String(brut ?? "")).replace(/\s/g, "").replace(",", ".");
    if (!t) return { valeur: 0, motif: "vide" };
    if (!/^\d{1,9}(\.\d{1,2})?$/.test(t)) return { valeur: 0, motif: "format" };
    const n = Number(t);
    if (n <= 0) return { valeur: n, motif: "nul" };
    return { valeur: Math.round(n * 100) / 100, motif: null };
}

/**
 * Où en est la facture : échue, et depuis combien de temps.
 *
 * Le retard se compte à partir du LENDEMAIN de l'échéance, comme le veut
 * l'article L. 441-10 : une facture payable le 15 n'est en retard que le 16.
 * Il sert aux pénalités, jamais à la prescription, qui a son propre point de
 * départ : voir `prescription`.
 */
export function retard(echeance, aujourdhui) {
    const jours = joursEntre(echeance, String(aujourdhui).slice(0, 10));
    if (jours === null) return null;
    return { jours: Math.max(0, jours), echue: jours > 0 };
}

/**
 * Le dernier jour du délai pour agir.
 *
 * Un délai en années s'éteint au même quantième, `ans` années plus tard, et la
 * prescription n'est acquise qu'une fois ce dernier jour accompli (article 2229
 * du code civil). Sans quantième identique, un 29 février par exemple, le délai
 * expire le dernier jour du mois, comme le prévoit l'article 641 du code de
 * procédure civile.
 */
export function dernierJourPourAgir(depuis, ans) {
    const d = jour(depuis);
    if (!d || !Number.isInteger(ans)) return null;
    const annee = d.a + ans;
    const finDuMois = new Date(Date.UTC(annee, d.m, 0)).getUTCDate();
    const quantieme = Math.min(d.j, finDuMois);
    return `${annee}-${String(d.m).padStart(2, "0")}-${String(quantieme).padStart(2, "0")}`;
}

/**
 * Le délai pour agir en paiement : depuis quand il court, et jusqu'à quand.
 *
 * Il part de la fin de la prestation, ou de la livraison, que le parcours
 * demande pour cette seule raison. Sans cette date, rien ne se tranche. Le
 * calcul ignore les interruptions (une action en justice, une reconnaissance de
 * la dette) : le parcours les mentionne, il ne peut pas les connaître.
 */
export function prescription(reponses, aujourdhui) {
    const r = reponses || {};
    const ans = PRESCRIPTION_ANS[r.client];
    const limite = dernierJourPourAgir(r.finPrestation, ans);
    if (!limite) return null;
    const joursRestants = joursEntre(String(aujourdhui).slice(0, 10), limite);
    return { ans, depuis: nettoyer(r.finPrestation), limite, joursRestants, acquise: joursRestants < 0 };
}

/** Le taux de pénalités applicable, et d'où il vient. */
export function tauxApplicable(reponses) {
    const r = reponses || {};
    if (r.client !== "professionnel") {
        return { taux: TAUX.legalCreancierPro, origine: "legal" };
    }
    if (r.tauxPrevu !== "oui") return { taux: TAUX_SUPPLETIF, origine: "suppletif" };

    const saisi = Number(String(r.tauxContrat ?? "").replace(",", "."));
    if (!Number.isFinite(saisi) || saisi <= 0) return { taux: TAUX_SUPPLETIF, origine: "suppletif" };
    // Un taux inférieur au plancher est réputé non écrit : c'est le plancher
    // qui s'applique, et non le taux que le contrat annonce.
    if (saisi < TAUX_PLANCHER) return { taux: TAUX_PLANCHER, origine: "plancher", annonce: saisi };
    return { taux: Math.round(saisi * 100) / 100, origine: "contrat" };
}

/**
 * Qui peut réclamer quoi, et qui ne peut plus rien réclamer.
 *
 * Deux portes fermées : la facture qui n'est pas encore échue, et celle que la
 * prescription a emportée. La première se voit dès l'échéance saisie ; la
 * seconde attend la date de fin de la prestation, d'où le délai court.
 */
export function concerne(reponses, aujourdhui) {
    const r = reponses || {};
    if (!r.client) return { concerne: null, motif: null };
    if (!nettoyer(r.echeance)) return { concerne: null, motif: null };

    const etat = retard(r.echeance, aujourdhui);
    if (!etat) return { concerne: null, motif: null };
    if (!etat.echue) return { concerne: false, motif: "pasEncoreEchue" };

    const delai = prescription(r, aujourdhui);
    if (!delai) return { concerne: null, motif: null };
    if (delai.acquise) return { concerne: false, motif: "prescrite" };
    return { concerne: true, motif: null };
}

/**
 * Le décompte de ce qui est dû, à la date du jour.
 *
 * Entre professionnels, les pénalités sont déjà acquises et se chiffrent. Avec
 * un particulier, elles ne commenceront à courir qu'à la réception de la
 * lettre : le décompte annonce donc un montant journalier à venir, pas un
 * arriéré. Confondre les deux ferait réclamer une somme qui n'est pas due.
 */
export function decompte(reponses, aujourdhui) {
    const r = reponses || {};
    if (!concerne(r, aujourdhui).concerne) return null;

    const principal = montant(r.montant);
    if (principal.motif) return null;

    const etat = retard(r.echeance, aujourdhui);
    const { taux, origine, annonce } = tauxApplicable(r);
    const professionnel = r.client === "professionnel";

    const penalites = professionnel
        ? Math.round(((principal.valeur * taux) / 100 / 365) * etat.jours * 100) / 100
        : 0;
    const indemnite = professionnel ? INDEMNITE_FORFAITAIRE : 0;
    const parJour = Math.round(((principal.valeur * taux) / 100 / 365) * 100) / 100;

    return {
        principal: principal.valeur,
        jours: etat.jours,
        taux,
        origineDuTaux: origine,
        tauxAnnonce: annonce,
        penalites,
        indemnite,
        parJour,
        total: Math.round((principal.valeur + penalites + indemnite) * 100) / 100,
        // Avec un particulier, rien ne court encore : la lettre le déclenche.
        interetsACourir: !professionnel,
    };
}

/** Ce qui manque, ce qui est refusé, et ce qui mérite un mot. */
export function valider(reponses, aujourdhui) {
    const r = reponses || {};
    const manques = [];
    const invalides = [];
    const alertes = [];

    const tri = concerne(r, aujourdhui);
    if (!tri.concerne) {
        // Tant que rien ne se tranche, nommer ce qui manque plutôt qu'un « client » passe-partout.
        let manque = "client";
        if (tri.concerne === null && r.client) {
            if (!jour(r.echeance)) manque = "echeance";
            else if (!jour(r.finPrestation)) manque = "finPrestation";
        }
        return { valide: false, manques: [manque], invalides, alertes };
    }

    const requis = [
        ["prenoms", r.prenoms],
        ["nom", r.nom],
        ["adresse", r.adresse],
        ["destinataire", r.destinataire],
        ["adresseDestinataire", r.adresseDestinataire],
        ["numeroFacture", r.numeroFacture],
        ["objet", r.objet],
        ["lieuDeSignature", r.lieuDeSignature],
    ];
    for (const [cle, valeur] of requis) {
        if (!nettoyer(valeur)) manques.push(cle);
    }

    const principal = montant(r.montant);
    if (principal.motif === "vide") manques.push("montant");
    else if (principal.motif) invalides.push({ champ: "montant", motif: principal.motif });

    for (const cle of ["dateFacture", "echeance", "dateDeSignature"]) {
        const brut = nettoyer(r[cle]);
        if (!brut) manques.push(cle);
        else if (!jour(brut)) invalides.push({ champ: cle, motif: "format" });
    }

    // Une facture ne peut pas échoir avant d'exister.
    const emission = joursEntre(r.dateFacture, r.echeance);
    if (emission !== null && emission < 0) {
        invalides.push({ champ: "echeance", motif: "avantLaFacture" });
    }

    if (r.client === "professionnel" && r.tauxPrevu === "oui") {
        const saisi = Number(String(r.tauxContrat ?? "").replace(",", "."));
        if (!nettoyer(r.tauxContrat)) manques.push("tauxContrat");
        else if (!Number.isFinite(saisi) || saisi <= 0 || saisi > 100) {
            invalides.push({ champ: "tauxContrat", motif: "format" });
        } else if (saisi < TAUX_PLANCHER) alertes.push("tauxSousLePlancher");
    }

    // La prescription approche : le dire quand il reste moins de six mois, compté
    // depuis la fin de la prestation et non depuis l'échéance.
    const delai = prescription(r, aujourdhui);
    if (delai && delai.joursRestants <= 183) alertes.push("prescriptionProche");

    return {
        valide: manques.length === 0 && invalides.length === 0,
        manques: [...new Set(manques)],
        invalides,
        alertes,
    };
}

/**
 * Un taux écrit à la française, virgule comprise.
 *
 * `${taux} %` rendait « 12.4 % » dans une lettre destinée à une entreprise : le
 * point décimal anglais au milieu d'un document officiel français.
 */
export function pourcent(taux) {
    return `${String(taux).replace(".", ",")} %`;
}

/**
 * Une adresse en plusieurs lignes, telle qu'elle a été saisie.
 *
 * `nettoyer` écrase les retours à la ligne avec le reste des blancs : découper
 * après lui rendait toujours une seule ligne, et l'adresse sortait en une
 * traite. Le découpage passe donc avant le nettoyage, ligne par ligne.
 */
export function lignesDAdresse(brut) {
    return String(brut ?? "")
        .split(/\r?\n/)
        .map((ligne) => nettoyer(ligne))
        .filter(Boolean);
}

/**
 * Un montant écrit comme une lettre l'écrit.
 *
 * Le séparateur de milliers rendu par `toLocaleString` en français est une
 * ESPACE FINE INSÉCABLE (U+202F), qui n'existe pas dans l'encodage WinAnsi des
 * polices standard d'un PDF : elle en sortirait comme un caractère de
 * substitution au milieu du montant réclamé. Elle est donc ramenée à l'espace
 * insécable ordinaire, qui tient la ligne aussi bien et que tout lecteur
 * connaît.
 */
export function euros(valeur) {
    const chiffres = valeur.toLocaleString("fr-FR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    return `${chiffres.replace(/ /g, " ")} €`;
}

/**
 * La lettre de mise en demeure.
 *
 * L'article 1344 du code civil demande « une sommation ou un acte portant
 * interpellation suffisante » : ce sont les mots qui comptent, pas un modèle.
 * La lettre dit donc ce qu'elle réclame, sur quel fondement, sous quel délai,
 * et ce qui suit à défaut. Elle se date et se signe, et part en recommandé avec
 * avis de réception, seule preuve de la date de réception.
 */
export function composerMiseEnDemeure(reponses, aujourdhui) {
    const r = reponses || {};
    const dateDeSignature = nettoyer(r.dateDeSignature) || String(aujourdhui).slice(0, 10);
    const complet = { ...r, dateDeSignature };
    if (!valider(complet, aujourdhui).valide) return null;

    const compte = decompte(complet, aujourdhui);
    const moi = identite({ prenoms: r.prenoms, nom: r.nom });
    const professionnel = r.client === "professionnel";

    const lignesDuDecompte = [
        `Principal restant dû : ${euros(compte.principal)}`,
        ...(professionnel
            ? [
                  `Pénalités de retard, ${pourcent(compte.taux)} l'an sur ${compte.jours} jours : ${euros(compte.penalites)}`,
                  `Indemnité forfaitaire pour frais de recouvrement : ${euros(compte.indemnite)}`,
                  `Total dû à ce jour : ${euros(compte.total)}`,
              ]
            : [`Total dû à ce jour : ${euros(compte.total)}`]),
    ];

    return {
        titre: "Mise en demeure de payer",
        blocs: [
            /* LA DISPOSITION D'UNE LETTRE ADMINISTRATIVE FRANÇAISE : expéditeur
               à gauche, destinataire à droite, lieu et date sous lui. Empilés à
               gauche, les deux blocs disaient les mêmes choses sans dire lequel
               écrivait à qui. Pas d'intitulés ici : ce sont des colonnes de
               lettre, pas les cases d'un formulaire. */
            {
                type: "entete",
                colonnes: [
                    {
                        lignes: [
                            moi,
                            ...lignesDAdresse(r.adresse),
                            "",
                            "Lettre recommandée avec avis de réception",
                        ],
                    },
                    {
                        lignes: [
                            nettoyer(r.destinataire),
                            ...lignesDAdresse(r.adresseDestinataire),
                            "",
                            `${nettoyer(r.lieuDeSignature)}, le ${dateEnToutesLettres(dateDeSignature)}`,
                        ],
                    },
                ],
            },
            {
                type: "paragraphe",
                texte: `Objet : mise en demeure de payer la facture n° ${nettoyer(r.numeroFacture)}`,
            },
            {
                type: "paragraphe",
                texte:
                    `Madame, Monsieur, ma facture n° ${nettoyer(r.numeroFacture)} du ` +
                    `${dateEnToutesLettres(r.dateFacture)}, d'un montant de ${eurosEnPhrase(compte.principal)} pour ` +
                    `${nettoyer(r.objet)}, était payable au plus tard le ${dateEnToutesLettres(r.echeance)}.`,
            },
            {
                type: "paragraphe",
                texte: `À ce jour, ${compte.jours} jours après cette échéance, elle demeure impayée.`,
            },
            {
                type: "paragraphe",
                texte: professionnel
                    ? "Conformément aux articles L. 441-10 et D. 441-5 du code de commerce, les pénalités " +
                      "de retard sont exigibles sans rappel, et une indemnité forfaitaire de 40 euros pour " +
                      "frais de recouvrement s'y ajoute. Les sommes dues s'établissent comme suit :"
                    : "Conformément à l'article 1344-1 du code civil, la présente mise en demeure fait " +
                      `courir les intérêts au taux légal, soit ${pourcent(TAUX.legalCreancierPro)} l'an, à compter de sa ` +
                      "réception. Les sommes dues s'établissent comme suit :",
            },
            { type: "lignes", lignes: lignesDuDecompte },
            {
                type: "paragraphe",
                texte:
                    `Je vous mets en demeure de me régler cette somme dans un délai de ${DELAI_MISE_EN_DEMEURE_JOURS} jours ` +
                    "à compter de la réception de la présente lettre.",
            },
            {
                type: "paragraphe",
                texte:
                    "À défaut, je saisirai la juridiction compétente par voie d'injonction de payer.",
            },
            { type: "paragraphe", texte: "Je vous prie d'agréer, Madame, Monsieur, mes salutations distinguées." },
            { type: "signature", nom: moi },
        ],
    };
}

/** Ce que le parcours annonce avant le téléchargement. */
export function recapitulatif(reponses, aujourdhui) {
    const compte = decompte(reponses, aujourdhui);
    if (!compte || !composerMiseEnDemeure(reponses, aujourdhui)) return null;
    const professionnel = reponses.client === "professionnel";
    const delai = prescription(reponses, aujourdhui);

    return [
        ["Facture", `n° ${nettoyer(reponses.numeroFacture)}, ${euros(compte.principal)}`],
        ["Retard", `${compte.jours} jours depuis l'échéance`],
        ...(professionnel
            ? [
                  ["Pénalités de retard", `${euros(compte.penalites)} au taux de ${pourcent(compte.taux)}`],
                  ["Indemnité forfaitaire", euros(compte.indemnite)],
              ]
            : [["Intérêts", `${pourcent(TAUX.legalCreancierPro)} l'an, à compter de la réception de la lettre`]]),
        ["Total réclamé", euros(compte.total)],
        ["Délai pour agir en justice", `jusqu'au ${dateEnToutesLettres(delai.limite)}, ${delai.ans} ans après la fin de la prestation`],
    ];
}
